/**
 * @bos/portal3 — the route table. A plain `RouteObject[]` (no browser router created
 * here) so it is importable and assertable in a node test without a DOM. `main.tsx` feeds
 * it to `createBrowserRouter`, which gives every page a real, addressable URL with working
 * Back / Forward / Refresh.
 *
 * M6 splits the chrome by audience:
 *   - `/`               → <IndexRedirect>: logged-out → sign-in, logged-in → dashboard.
 *   - /login, /register → inside <AuthLayout> (the branded unauthenticated entry),
 *                          wrapped in <RedirectIfAuthed> so a signed-in user is bounced on.
 *   - /app, /account,
 *     /app/projects/:id,
 *     /app/apps/:id     → inside <AppShell>, which gates auth once (the shell redirects an
 *                          anonymous visitor to the sign-in entry) and frames every page.
 *
 * A RUN IS A BUILDING WITH ROOMS. `/app/runs/:id` is no longer one page but a layout —
 * <RunShell> — with the run's rooms as nested routes under it. Each room is a real
 * address that survives a refresh (it rebuilds from its own read surface), and the old
 * URL still lands where it always did: the index route is the Overview.
 */
import type { RouteObject } from 'react-router-dom';
import { AppShell } from './AppShell';
import { AuthLayout } from './AuthLayout';
import { IndexRedirect } from './pages/IndexRedirect';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { AccountPage } from './pages/AccountPage';
import { ProjectPage } from './pages/ProjectPage';
import { AppPage } from './pages/AppPage';
import { RunShell } from './pages/RunShell';
import { AdminPage } from './pages/AdminPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RedirectIfAuthed } from './auth/guards';
import { OverviewRoom } from './runs/rooms/OverviewRoom';
import { DataModelRoom } from './runs/rooms/DataModelRoom';
import { StageRoom } from './runs/rooms/StageRoom';
import { CodeRoom, ScreensRoom } from './runs/rooms/BuildRooms';
import { VerdictRoom } from './runs/rooms/VerdictRoom';
import { JournalRoom } from './runs/rooms/JournalRoom';

/** Canonical paths, shared by the nav links and the route-table smoke test. */
export const PATHS = {
  landing: '/',
  login: '/login',
  register: '/register',
  dashboard: '/app',
  account: '/account',
  /** The admin area (run limits). The SERVER refuses non-admins on every admin route;
   *  the client merely also hides the door (AppShell shows the link to admins only). */
  admin: '/admin',
  /** The parameterized project route (as react-router sees it). */
  projectPattern: '/app/projects/:id',
  /** Build the addressable URL for one project. */
  project: (id: string): string => `/app/projects/${id}`,
  /** The parameterized app route (as react-router sees it). An app is a thing in its own
   *  right — one business may have several — so it gets its own addressable URL. */
  appPattern: '/app/apps/:id',
  /** Build the addressable URL for one app. */
  app: (id: string): string => `/app/apps/${id}`,
  /** The parameterized run (shell) route (as react-router sees it). */
  runPattern: '/app/runs/:id',
  /** Build the addressable URL for one run — its Overview, the front desk. */
  run: (id: string): string => `/app/runs/${id}`,
  /** Build the addressable URL for one ROOM of a run (`''` is the Overview itself). */
  runRoom: (id: string, slug: string): string =>
    slug === '' ? `/app/runs/${id}` : `/app/runs/${id}/${slug}`,
} as const;

/** The run's rooms, as route children of <RunShell>. The slugs are the ones `ROOMS`
 *  (runs/model.ts) names — the co-located test holds the two lists to each other. */
const runRoomRoutes: RouteObject[] = [
  { index: true, element: <OverviewRoom /> },
  { path: 'plan/data-model', element: <DataModelRoom /> },
  { path: 'plan/lifecycles', element: <StageRoom stage="statemachine" /> },
  { path: 'plan/operations', element: <StageRoom stage="process" /> },
  { path: 'plan/rules', element: <StageRoom stage="rules" /> },
  { path: 'plan/permissions', element: <StageRoom stage="rbac" /> },
  { path: 'plan/audit', element: <StageRoom stage="audit" /> },
  { path: 'plan/modules', element: <StageRoom stage="decomposition" /> },
  { path: 'build/code', element: <CodeRoom /> },
  { path: 'build/screens', element: <ScreensRoom /> },
  { path: 'verdict', element: <VerdictRoom /> },
  { path: 'journal', element: <JournalRoom /> },
];

export const routes: RouteObject[] = [
  {
    path: '/',
    children: [
      { index: true, element: <IndexRedirect /> },
      {
        element: <AuthLayout />,
        children: [
          {
            path: 'login',
            element: (
              <RedirectIfAuthed>
                <LoginPage />
              </RedirectIfAuthed>
            ),
          },
          {
            path: 'register',
            element: (
              <RedirectIfAuthed>
                <RegisterPage />
              </RedirectIfAuthed>
            ),
          },
        ],
      },
      {
        element: <AppShell />,
        children: [
          { path: 'app', element: <DashboardPage /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'admin', element: <AdminPage /> },
          { path: 'app/projects/:id', element: <ProjectPage /> },
          { path: 'app/apps/:id', element: <AppPage /> },
          { path: 'app/runs/:id', element: <RunShell />, children: runRoomRoutes },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
